import { Payroll } from "src/redux/types/payroll";

const n = (val: string | number | null | undefined): number =>
  parseFloat(String(val ?? 0).replace(/[^0-9.-]/g, "")) || 0;

const Row = ({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string | number | null | undefined;
  highlight?: boolean;
}) => {
  const amount = n(value);
  if (!amount) return null; // hide zero / null rows automatically
  return (
    <div
      className={`flex justify-between items-center py-2.5 border-b border-dashed border-slate-100 last:border-0 ${
        highlight ? "font-semibold text-slate-800" : "text-slate-600"
      }`}
    >
      <span className="text-sm">{label}</span>
      <span
        className={`text-sm tabular-nums ${highlight ? "text-slate-900" : ""}`}
      >
        ₹{amount.toLocaleString("en-IN")}
      </span>
    </div>
  );
};

interface MonthlyPayrollProps {
  payroll: Payroll;
}

export default function MonthlyPayroll({ payroll }: MonthlyPayrollProps) {

  const totalEarnings =
    n(payroll.basic) +
    n(payroll.hra) +
    n(payroll.special_allowance) +
    n(payroll.misc_allowance) +
    n(payroll.statutory_bonus) +
    n(payroll.incentives) +
    n(payroll.otherEarnings) +
    n(payroll.travel_allowances) +
    n(payroll.reimbursementOthers) +
    n(payroll.reimbursementTravel);

  const totalDeductions =
    n(payroll.pf) +
    n(payroll.esic) +
    n(payroll.incomeTaxDeduction) +
    n(payroll.health_deduction) +
    n(payroll.other_deduction) +
    n(payroll.lop_deduction) +
    n(payroll.ap_tax);

  const netSalary = n(payroll.net_salary);

  return (
    <div className="w-full mx-auto font-sans">
      {/* Top accent bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-slate-800 via-slate-600 to-slate-400 rounded-t-2xl" />

      <div className="bg-white border border-slate-200 border-t-0 rounded-b-2xl shadow-xl overflow-hidden">
        {/* ── Header ── */}
        <div className="bg-slate-900 px-8 py-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-slate-400 text-xs uppercase tracking-widest font-semibold mb-1">
              Pay Slip
            </p>
            <h2 className="text-2xl font-bold text-white capitalize tracking-tight">
              {payroll.name}
            </h2>
            <p className="text-slate-400 text-sm mt-0.5">
              {payroll.designation}
              {payroll.department ? ` · ${payroll.department}` : ""}
            </p>
          </div>

          <div className="text-left md:text-right space-y-1">
            <p className="text-white font-semibold text-lg">
              {payroll.month} {payroll.year}
            </p>
            {payroll.pay_date && (
              <p className="text-slate-400 text-xs">
                Paid on{" "}
                {new Date(payroll.pay_date).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            )}
            <p className="text-slate-400 text-xs">
              Code: {payroll.employee_code ?? "N/A"}
            </p>
            {payroll.date_of_joining && (
              <p className="text-slate-400 text-xs">
                DOJ:{" "}
                {new Date(payroll.date_of_joining).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            )}
          </div>
        </div>

        {/* ── Attendance strip ── */}
        <div className="grid grid-cols-2 divide-x divide-slate-100 bg-slate-50 border-b border-slate-100">
          <div className="px-8 py-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <svg
                className="w-4 h-4 text-emerald-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium">Days Worked</p>
              <p className="text-slate-800 font-bold text-lg leading-tight">
                {payroll.days_worked ?? "—"}
              </p>
            </div>
          </div>
          <div className="px-8 py-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center">
              <svg
                className="w-4 h-4 text-rose-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium">LOP Days</p>
              <p className="text-slate-800 font-bold text-lg leading-tight">
                {payroll.lop ?? "—"}
              </p>
            </div>
          </div>
        </div>

        {/* ── Earnings & Deductions ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100 px-0">
          {/* Earnings */}
          <div className="px-8 py-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Earnings
              </h3>
            </div>
            <div>
              <Row label="Basic" value={payroll.basic} />
              <Row label="HRA" value={payroll.hra} />
              <Row
                label="Special Allowance"
                value={payroll.special_allowance}
              />
              <Row label="Misc Allowance" value={payroll.misc_allowance} />
              <Row label="Statutory Bonus" value={payroll.statutory_bonus} />
              <Row label="Incentives" value={payroll.incentives} />
              <Row label="Travel Allowance" value={payroll.travel_allowances} />
              <Row label="Other Earnings" value={payroll.otherEarnings} />
              <Row
                label="Reimbursement (Travel)"
                value={payroll.reimbursementTravel}
              />
              <Row
                label="Reimbursement (Others)"
                value={payroll.reimbursementOthers}
              />
            </div>
            <div className="mt-4 flex justify-between items-center bg-emerald-50 rounded-xl px-4 py-3 border border-emerald-100">
              <span className="text-sm font-bold text-emerald-700">
                Total Earnings
              </span>
              <span className="text-sm font-black text-emerald-700 tabular-nums">
                ₹{totalEarnings.toLocaleString("en-IN")}
              </span>
            </div>
          </div>

          {/* Deductions */}
          <div className="px-8 py-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-rose-400" />
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Deductions
              </h3>
            </div>
            <div>
              <Row label="Provident Fund" value={payroll.pf} />
              <Row label="ESIC" value={payroll.esic} />
              <Row
                label="Income Tax (TDS)"
                value={payroll.incomeTaxDeduction}
              />
              <Row label="Health Deduction" value={payroll.health_deduction} />
              <Row label="LOP Deduction" value={payroll.lop_deduction} />
              <Row label="AP Tax" value={payroll.ap_tax} />
              <Row label="Other Deductions" value={payroll.other_deduction} />
            </div>
            <div className="mt-4 flex justify-between items-center bg-rose-50 rounded-xl px-4 py-3 border border-rose-100">
              <span className="text-sm font-bold text-rose-600">
                Total Deductions
              </span>
              <span className="text-sm font-black text-rose-600 tabular-nums">
                ₹{totalDeductions.toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </div>

        {/* ── Net Salary ── */}
        <div className="mx-6 mb-6 rounded-2xl bg-slate-900 px-8 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-slate-400 text-xs uppercase tracking-widest font-semibold mb-1">
              Net Salary Payable
            </p>
            <p className="text-4xl font-black text-white tracking-tight">
              ₹{netSalary.toLocaleString("en-IN")}
            </p>
            {payroll.totalNetPayableWords && (
              <p className="text-slate-400 text-xs mt-1 italic">
                {payroll.totalNetPayableWords}
              </p>
            )}
          </div>
          <div className="flex gap-6 text-center">
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">
                Gross
              </p>
              <p className="text-white font-bold tabular-nums">
                ₹{totalEarnings.toLocaleString("en-IN")}
              </p>
            </div>
            <div className="w-px bg-slate-700" />
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">
                Deductions
              </p>
              <p className="text-rose-400 font-bold tabular-nums">
                −₹{totalDeductions.toLocaleString("en-IN")}
              </p>
            </div>
          </div>
        </div>

        {/* ── Bank & Tax ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-6 pb-8">
          <div className="rounded-xl border border-slate-100 bg-slate-50 p-5 space-y-2">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Bank Details
            </h3>
            {[
              ["Bank", payroll.bank_name],
              ["Branch", payroll.branch],
              ["A/C Number", payroll.ac_number],
              ["IFSC Code", payroll.ifsc_code],
            ].map(([label, val]) =>
              val ? (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-slate-400">{label}</span>
                  <span className="text-slate-700 font-medium">{val}</span>
                </div>
              ) : null,
            )}
          </div>

          <div className="rounded-xl border border-slate-100 bg-slate-50 p-5 space-y-2">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Tax & IDs
            </h3>
            {[
              ["PAN", payroll.pan],
              ["PF Account", payroll.pf_account_number],
              ["ESIC UAN", payroll.esic_uan],
              ["UAN", payroll.uan_number],
            ].map(([label, val]) =>
              val ? (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-slate-400">{label}</span>
                  <span className="text-slate-700 font-mono font-medium">
                    {val}
                  </span>
                </div>
              ) : null,
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
